import { NavLink } from "react-router-dom";
import { Bot, Folder, Notebook, PanelLeftClose, PanelLeftOpen, Plus } from "lucide-react";
import { ManifoldFullMonoIcon, ManifoldMonoBadgeIcon } from "~/assets/icons";
import { ManifoldNextGenAccountMenu } from "~/components/ManifoldNextGenAccountMenu";
import { IconButton } from "~/components/assistant/PopoverButton";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

interface ManifoldNextGenSideNavProps {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  disableExpand?: boolean;
  isDark: boolean;
  onToggleDark: () => void;
  onSwitchToClassic: () => void;
  className?: string;
}

interface SideNavItemProps {
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
  disabled?: boolean;
  active?: boolean;
  tooltip?: string;
  className?: string;
}

function SideNavItem({
  icon,
  label,
  collapsed,
  disabled,
  active,
  tooltip,
  className,
}: SideNavItemProps) {
  return (
    <Tooltip delayDuration={500}>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "flex h-8 items-center rounded-md text-sm whitespace-nowrap",
            collapsed ? "w-fit px-2" : "w-full gap-2.5 px-2",
            disabled && "cursor-default opacity-70",
            !disabled &&
              !active &&
              "cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            active && "bg-sidebar-accent text-sidebar-accent-foreground font-medium",
            !active && "text-muted-foreground",
            className
          )}
        >
          <span className="shrink-0">{icon}</span>
          {!collapsed && label}
        </span>
      </TooltipTrigger>
      {collapsed && <TooltipContent side="right">{tooltip ?? label}</TooltipContent>}
    </Tooltip>
  );
}

export function ManifoldNextGenSideNav({
  collapsed,
  onCollapsedChange,
  disableExpand = false,
  isDark,
  onToggleDark,
  onSwitchToClassic,
  className,
}: ManifoldNextGenSideNavProps) {
  return (
    <nav
      aria-label="Manifold NextGen primary navigation"
      className={cn(
        "flex h-full shrink-0 flex-col border-r border-border bg-sidebar text-sidebar-foreground",
        "overflow-hidden transition-[width] duration-200 ease-in-out",
        collapsed ? "w-14" : "w-50",
        className
      )}
    >
      {collapsed ? (
        <div className="flex pb-2 pl-[14px] pr-2 pt-3">
          <div className="group relative flex h-[30px] items-center">
            <ManifoldMonoBadgeIcon
              className={cn(
                "size-7 transition-opacity duration-200",
                !disableExpand && "group-hover:opacity-0"
              )}
            />
            {!disableExpand && (
              <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                <IconButton
                  variant="ghost"
                  onClick={() => onCollapsedChange(false)}
                  aria-label="Expand sidebar"
                  data-testid="expand-sidebar"
                >
                  <PanelLeftOpen className="size-4.5" />
                </IconButton>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between pb-2 pl-[14px] pr-2 pt-3">
          <div className="flex items-center">
            <ManifoldFullMonoIcon
              className="h-6 w-auto shrink-0"
              aria-hidden="true"
              data-testid="manifold-logo"
            />
          </div>
          <IconButton
            variant="ghost"
            onClick={() => onCollapsedChange(true)}
            aria-label="Collapse sidebar"
            data-testid="collapse-sidebar"
          >
            <PanelLeftClose className="size-4.5" />
          </IconButton>
        </div>
      )}

      <ul className="mt-2 flex flex-col gap-1 px-3">
        <li>
          <SideNavItem
            icon={<Plus size={16} />}
            label="New task"
            collapsed={collapsed}
            disabled
            tooltip="New task"
            className="bg-muted/50"
          />
        </li>
        <li>
          <NavLink to="/nextgen/projects">
            {({ isActive }) => (
              <SideNavItem
                icon={<Notebook size={16} />}
                label="Projects"
                collapsed={collapsed}
                active={isActive}
              />
            )}
          </NavLink>
        </li>
        <li>
          <NavLink to="/nextgen/datasets">
            {({ isActive }) => (
              <SideNavItem
                icon={<Folder size={16} />}
                label="Datasets"
                collapsed={collapsed}
                active={isActive}
              />
            )}
          </NavLink>
        </li>
        <li>
          <NavLink to="/nextgen/agents">
            {({ isActive }) => (
              <SideNavItem
                icon={<Bot size={16} />}
                label="Agents"
                collapsed={collapsed}
                active={isActive}
              />
            )}
          </NavLink>
        </li>
      </ul>
      <div className="mt-auto px-2 pb-3">
        <ManifoldNextGenAccountMenu
          collapsed={collapsed}
          isDark={isDark}
          onToggleDark={onToggleDark}
          onSwitchToClassic={onSwitchToClassic}
        />
      </div>
    </nav>
  );
}
